export const internalLinkClusters = {
  mapleOsAiOperatingSystem: {
    label: 'MapleOS / AI Operating System',
    description: 'Operational AI, orchestration, model routing, and governed working environments.',
  },
  mapleNodeEdgeAi: {
    label: 'MapleNode / Edge AI',
    description: 'Edge deployment, local inference, regional workflows, and appliance-based AI delivery.',
  },
  sovereignAiCanadianInfrastructure: {
    label: 'Sovereign AI / Canadian Infrastructure',
    description: 'Canadian jurisdiction, residency, infrastructure control, and sovereign deployment strategy.',
  },
  slmModelTraining: {
    label: 'SLM / Model Training',
    description: 'Small language models, fine-tuning, pre-training, and specialized model adaptation.',
  },
  privateAiInfrastructure: {
    label: 'Private AI Infrastructure',
    description: 'Private workspaces, secure knowledge bases, controlled deployment, and enterprise governance.',
  },
  healthcareRegulatedIndustries: {
    label: 'Healthcare / Regulated Industries',
    description: 'Clinical, institutional, and regulated deployment contexts with strong governance requirements.',
  },
  legalConfidentialWorkflows: {
    label: 'Legal / Confidential Workflows',
    description: 'Confidential legal work, privilege-sensitive deployment, and defensible AI operations.',
  },
  defenceSecureDeployment: {
    label: 'Defence / Secure Deployment',
    description: 'High-trust, high-control deployment patterns for defence-adjacent and restricted environments.',
  },
  industrialOperationalAi: {
    label: 'Industrial / Operational AI',
    description: 'Industrial procedures, field operations, maintenance knowledge, and resilient deployment.',
  },
  complianceGovernance: {
    label: 'Compliance / Governance',
    description: 'Residency, privacy, auditability, security, and policy-aligned AI operations.',
  },
} as const;

export type InternalLinkClusterKey = keyof typeof internalLinkClusters;

export type EditorialLinkSuggestion = {
  anchor: string;
  href: string;
  kind: 'product' | 'concept' | 'insight' | 'commercial' | 'industry';
};

export type EditorialLinkMetadata = {
  cluster: InternalLinkClusterKey;
  priorityDestinations: string[];
  suggestedInBodyLinks: EditorialLinkSuggestion[];
  commercialDestination: string;
  linkingNotes?: string[];
};

export const internalLinkingChecklist = {
  maxInBodyLinks: 7,
  recommendedInBodyLinks: '5–7 for standard articles and topic pages',
  firstParagraphRule: 'Avoid loading the first paragraph with multiple links; add the first link after context is established.',
  duplicateDestinationRule: 'Link the first meaningful mention of a destination and avoid repeating that destination in the body unless the page is unusually long.',
  requiredLinkCategories: ['product page', 'core concept page', 'two related insight articles where appropriate', 'commercial/contact/training destination where appropriate'],
  discouragedAnchors: ['click here', 'learn more'],
  preferredAnchorExamples: [
    'MapleOS AI Operating System',
    'MapleNode edge AI appliance',
    'private AI infrastructure',
    'small language model training',
    'Canadian sovereign AI',
  ],
} as const;

const clusterEditorialDefaults: Record<
  InternalLinkClusterKey,
  {
    priorityDestinations: string[];
    suggestedInBodyLinks: EditorialLinkSuggestion[];
    commercialDestination: string;
    linkingNotes: string[];
  }
> = {
  mapleOsAiOperatingSystem: {
    priorityDestinations: ['/mapleos', '/canadian-ai-platform', '/private-ai', '/insights/future-of-ai-is-operational-not-conversational'],
    suggestedInBodyLinks: [
      { anchor: 'MapleOS AI Operating System', href: '/mapleos', kind: 'product' },
      { anchor: 'operational AI', href: '/insights/future-of-ai-is-operational-not-conversational', kind: 'insight' },
      { anchor: 'AI orchestration', href: '/insights/ai-orchestration-explained-beyond-chatbots', kind: 'insight' },
      { anchor: 'Canadian AI platform', href: '/canadian-ai-platform', kind: 'concept' },
    ],
    commercialDestination: '/enterprise',
    linkingNotes: ['Use MapleOS as the main product destination and connect it to operational AI, orchestration, and private deployment context.'],
  },
  mapleNodeEdgeAi: {
    priorityDestinations: ['/maplenode', '/mapleos', '/private-ai', '/canadian-ai-infrastructure'],
    suggestedInBodyLinks: [
      { anchor: 'MapleNode edge AI appliance', href: '/maplenode', kind: 'product' },
      { anchor: 'Canadian AI infrastructure', href: '/canadian-ai-infrastructure', kind: 'concept' },
      { anchor: 'What Is an Edge AI Appliance?', href: '/insights/what-is-an-edge-ai-appliance-maplenode', kind: 'insight' },
      { anchor: 'private AI infrastructure', href: '/private-ai', kind: 'concept' },
    ],
    commercialDestination: '/enterprise',
    linkingNotes: ['Use MapleNode when the page discusses local inference, edge deployment, field workflows, or restricted connectivity environments.'],
  },
  sovereignAiCanadianInfrastructure: {
    priorityDestinations: ['/sovereign-ai-canada', '/canadian-ai-infrastructure', '/private-ai', '/data-residency'],
    suggestedInBodyLinks: [
      { anchor: 'Canadian sovereign AI', href: '/sovereign-ai-canada', kind: 'concept' },
      { anchor: 'Canadian AI infrastructure', href: '/canadian-ai-infrastructure', kind: 'concept' },
      { anchor: 'private AI infrastructure', href: '/private-ai', kind: 'concept' },
      { anchor: 'Canadian AI data residency', href: '/data-residency', kind: 'concept' },
    ],
    commercialDestination: '/enterprise',
    linkingNotes: ['Bias links toward sovereignty, infrastructure, jurisdiction, and residency rather than generic AI language.'],
  },
  slmModelTraining: {
    priorityDestinations: ['/ai-model-training', '/small-language-model-training', '/private-ai', '/rag-vs-fine-tuning'],
    suggestedInBodyLinks: [
      { anchor: 'AI model training', href: '/ai-model-training', kind: 'product' },
      { anchor: 'small language model training', href: '/small-language-model-training', kind: 'product' },
      { anchor: 'RAG vs fine-tuning', href: '/rag-vs-fine-tuning', kind: 'concept' },
      { anchor: 'private AI infrastructure', href: '/private-ai', kind: 'concept' },
    ],
    commercialDestination: '/purchase',
    linkingNotes: ['Use training pages as the commercial path when content explains behaviour change, specialization, or domain ownership.'],
  },
  privateAiInfrastructure: {
    priorityDestinations: ['/private-ai', '/mapleos', '/maplenode', '/security'],
    suggestedInBodyLinks: [
      { anchor: 'private AI infrastructure', href: '/private-ai', kind: 'product' },
      { anchor: 'MapleOS AI Operating System', href: '/mapleos', kind: 'product' },
      { anchor: 'MapleNode edge AI appliance', href: '/maplenode', kind: 'product' },
      { anchor: 'AI security', href: '/security', kind: 'concept' },
    ],
    commercialDestination: '/enterprise',
    linkingNotes: ['Connect private AI to secure knowledge, role-based access, and deployment control.'],
  },
  healthcareRegulatedIndustries: {
    priorityDestinations: ['/healthcare-ai-canada', '/private-ai', '/ai-model-training', '/data-residency'],
    suggestedInBodyLinks: [
      { anchor: 'Healthcare AI Canada', href: '/healthcare-ai-canada', kind: 'industry' },
      { anchor: 'private AI infrastructure', href: '/private-ai', kind: 'product' },
      { anchor: 'AI model training', href: '/ai-model-training', kind: 'product' },
      { anchor: 'Canadian AI data residency', href: '/data-residency', kind: 'concept' },
    ],
    commercialDestination: '/enterprise',
    linkingNotes: ['Healthcare pages should balance governance, privacy, local jurisdiction, and model specialization.'],
  },
  legalConfidentialWorkflows: {
    priorityDestinations: ['/legal-ai-canada', '/private-ai', '/data-residency', '/ai-model-training'],
    suggestedInBodyLinks: [
      { anchor: 'Legal AI Canada', href: '/legal-ai-canada', kind: 'industry' },
      { anchor: 'private AI infrastructure', href: '/private-ai', kind: 'product' },
      { anchor: 'Canadian AI data residency', href: '/data-residency', kind: 'concept' },
      { anchor: 'AI model training', href: '/ai-model-training', kind: 'product' },
    ],
    commercialDestination: '/enterprise',
    linkingNotes: ['Legal pages should prioritize confidentiality, privilege, residency, and defensible workflow language.'],
  },
  defenceSecureDeployment: {
    priorityDestinations: ['/private-ai', '/canadian-ai-infrastructure', '/ai-model-training', '/security'],
    suggestedInBodyLinks: [
      { anchor: 'secure deployment', href: '/security', kind: 'concept' },
      { anchor: 'Canadian AI infrastructure', href: '/canadian-ai-infrastructure', kind: 'concept' },
      { anchor: 'AI model training', href: '/ai-model-training', kind: 'product' },
      { anchor: 'private AI infrastructure', href: '/private-ai', kind: 'product' },
    ],
    commercialDestination: '/enterprise',
    linkingNotes: ['Use careful language around secure deployment, restricted environments, and controlled inference.'],
  },
  industrialOperationalAi: {
    priorityDestinations: ['/maplenode', '/private-ai', '/ai-model-training', '/canadian-ai-infrastructure'],
    suggestedInBodyLinks: [
      { anchor: 'MapleNode edge AI appliance', href: '/maplenode', kind: 'product' },
      { anchor: 'AI model training', href: '/ai-model-training', kind: 'product' },
      { anchor: 'private AI infrastructure', href: '/private-ai', kind: 'product' },
      { anchor: 'Canadian AI infrastructure', href: '/canadian-ai-infrastructure', kind: 'concept' },
    ],
    commercialDestination: '/enterprise',
    linkingNotes: ['Industrial pages should emphasize resilience, procedures, deployment flexibility, and worksite proximity.'],
  },
  complianceGovernance: {
    priorityDestinations: ['/data-residency', '/pipeda-compliant-ai', '/security', '/compliance'],
    suggestedInBodyLinks: [
      { anchor: 'Canadian AI data residency', href: '/data-residency', kind: 'concept' },
      { anchor: 'PIPEDA-compliant AI', href: '/pipeda-compliant-ai', kind: 'concept' },
      { anchor: 'AI security', href: '/security', kind: 'concept' },
      { anchor: 'AI compliance', href: '/compliance', kind: 'concept' },
    ],
    commercialDestination: '/enterprise',
    linkingNotes: ['Use compliance links to strengthen governance clusters without creating repetitive legalistic anchor text.'],
  },
};

export function buildEditorialLinkMetadata(input: {
  cluster: InternalLinkClusterKey;
  priorityDestinations?: string[];
  suggestedInBodyLinks?: EditorialLinkSuggestion[];
  commercialDestination?: string;
  linkingNotes?: string[];
}): EditorialLinkMetadata {
  const defaults = clusterEditorialDefaults[input.cluster];

  return {
    cluster: input.cluster,
    priorityDestinations: input.priorityDestinations ?? defaults.priorityDestinations,
    suggestedInBodyLinks: input.suggestedInBodyLinks ?? defaults.suggestedInBodyLinks,
    commercialDestination: input.commercialDestination ?? defaults.commercialDestination,
    linkingNotes: input.linkingNotes ?? defaults.linkingNotes,
  };
}
